import React, { useState } from "react";

const ProcedureForm = ({
  procedure,
  onClose,
  onSave,
  departments,
  labTests,
  medicines,
}) => {
  const [formData, setFormData] = useState(
    procedure || {
      id: "",
      procedureCode: "",
      procedureName: "",
      departmentID: "",
      baseCost: "",
      additionalCosts: [],
      preProcedureChecklist: [],
      postProcedureCare: [],
      requiredResources: [],
      insuranceMapping: [],
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleArrayChange = (fieldName, index, value) => {
    const updatedArray = [...formData[fieldName]];
    updatedArray[index] = value;
    setFormData({ ...formData, [fieldName]: updatedArray });
  };

  const handleArrayAdd = (fieldName) => {
    setFormData({ ...formData, [fieldName]: [...formData[fieldName], ""] });
  };

  const handleArrayRemove = (fieldName, index) => {
    const updatedArray = formData[fieldName].filter((_, i) => i !== index);
    setFormData({ ...formData, [fieldName]: updatedArray });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.id) {
      formData.id = Date.now().toString(); // Generate unique ID
    }
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-4/5 max-h-[90%] flex flex-col">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {procedure ? "Edit Procedure" : "Add New Procedure"}
        </h2>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-grow mb-4 pr-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-600 mb-2">
                  Procedure Name
                </label>
                <input
                  type="text"
                  name="procedureName"
                  value={formData.procedureName}
                  onChange={handleChange}
                  placeholder="Enter Procedure Name"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-600 mb-2">Department</label>
                <select
                  name="departmentID"
                  value={formData.departmentID}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                >
                  <option value="" disabled>
                    Select Department
                  </option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-gray-600 mb-2">Base Cost</label>
                <input
                  type="number"
                  name="baseCost"
                  value={formData.baseCost}
                  onChange={handleChange}
                  placeholder="Enter Base Cost"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Additional Costs */}
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Additional Costs
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                  {formData.additionalCosts.map((cost, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <input
                        type="text"
                        value={cost}
                        onChange={(e) =>
                          handleArrayChange(
                            "additionalCosts",
                            index,
                            e.target.value
                          )
                        }
                        placeholder="Enter Additional Cost"
                        className="flex-grow px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[rgba(199,234,201,0.8)] focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          handleArrayRemove("additionalCosts", index)
                        }
                        className="px-3 py-2 text-black rounded-lg shadow hover:opacity-90"
                        style={{ backgroundColor: "#ff6b6b" }} // Red for remove
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => handleArrayAdd("additionalCosts")}
                  className="mt-4 px-4 py-2 text-black rounded-lg shadow hover:opacity-90"
                  style={{ backgroundColor: "#c7eac9" }}
                >
                  Add Cost
                </button>
              </div>

              {/* Pre-Procedure Checklist */}
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Pre-Procedure Checklist
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                  {formData.preProcedureChecklist.map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <select
                        value={item}
                        onChange={(e) =>
                          handleArrayChange(
                            "preProcedureChecklist",
                            index,
                            e.target.value
                          )
                        }
                        className="flex-grow px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[rgba(199,234,201,0.8)] focus:outline-none"
                      >
                        <option value="" disabled>
                          Select Lab Test
                        </option>
                        {labTests.map((test) => (
                          <option key={test.id} value={test.name}>
                            {test.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() =>
                          handleArrayRemove("preProcedureChecklist", index)
                        }
                        className="px-3 py-2 text-black rounded-lg shadow hover:opacity-90"
                        style={{ backgroundColor: "#ff6b6b" }} // Red for remove
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => handleArrayAdd("preProcedureChecklist")}
                  className="mt-4 px-4 py-2 text-black rounded-lg shadow hover:opacity-90"
                  style={{ backgroundColor: "#c7eac9" }}
                >
                  Add Test
                </button>
              </div>

              {/* Post-Procedure Care */}
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Post-Procedure Care
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                  {formData.postProcedureCare.map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <select
                        value={item}
                        onChange={(e) =>
                          handleArrayChange(
                            "postProcedureCare",
                            index,
                            e.target.value
                          )
                        }
                        className="flex-grow px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[rgba(199,234,201,0.8)] focus:outline-none"
                      >
                        <option value="" disabled>
                          Select Medicine
                        </option>
                        {medicines.map((med) => (
                          <option key={med.id} value={med.name}>
                            {med.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() =>
                          handleArrayRemove("postProcedureCare", index)
                        }
                        className="px-3 py-2 text-black rounded-lg shadow hover:opacity-90"
                        style={{ backgroundColor: "#ff6b6b" }} // Red for remove
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => handleArrayAdd("postProcedureCare")}
                  className="mt-4 px-4 py-2 text-black rounded-lg shadow hover:opacity-90"
                  style={{ backgroundColor: "#c7eac9" }}
                >
                  Add Medicine
                </button>
              </div>

              {/* Required Resources */}
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Required Resources
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                  {formData.requiredResources.map((resource, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <input
                        type="text"
                        value={resource}
                        onChange={(e) =>
                          handleArrayChange(
                            "requiredResources",
                            index,
                            e.target.value
                          )
                        }
                        placeholder="Enter Resource"
                        className="flex-grow px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[rgba(199,234,201,0.8)] focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          handleArrayRemove("requiredResources", index)
                        }
                        className="px-3 py-2 text-black rounded-lg shadow hover:opacity-90"
                        style={{ backgroundColor: "#ff6b6b" }} // Red for remove
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => handleArrayAdd("requiredResources")}
                  className="mt-4 px-4 py-2 text-black rounded-lg shadow hover:opacity-90"
                  style={{ backgroundColor: "#c7eac9" }}
                >
                  Add Resource
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Fixed Buttons */}
        <div className="flex justify-end border-t pt-4">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-400 text-black px-4 py-2 rounded-lg hover:bg-gray-500 mr-4"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="bg-green-500 text-black px-4 py-2 rounded-lg hover:bg-green-600"
          >
            Save Procedure
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProcedureForm;
